import { useMemo, useState } from 'react'
import {
  Activity,
  AlertCircle,
  CheckCircle2,
  RefreshCw,
  TrendingUp,
  XCircle,
} from 'lucide-react'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { useMonitoringMetrics, useMonitoringHealth } from '@/hooks/useMonitoring'
import { cn } from '@/lib/utils'

// ─── ユーティリティ ───

function formatPercentage(value: number): string {
  return `${(value * 100).toFixed(1)}%`
}

function formatDateTime(isoString: string | null): string {
  if (!isoString) return '—'
  const d = new Date(isoString)
  return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

// ─── サブコンポーネント ───

interface MetricCardProps {
  title: string
  value: string
  sub?: string
  status: 'good' | 'warning' | 'danger' | 'neutral'
  icon: React.ReactNode
}

function MetricCard({ title, value, sub, status, icon }: MetricCardProps) {
  const bgColor = {
    good: 'bg-green-50 border-green-200',
    warning: 'bg-yellow-50 border-yellow-200',
    danger: 'bg-red-50 border-red-200',
    neutral: 'bg-card border-border',
  }[status]

  const textColor = {
    good: 'text-green-700',
    warning: 'text-yellow-700',
    danger: 'text-red-700',
    neutral: 'text-foreground',
  }[status]

  return (
    <div className={cn('rounded-lg border-2 p-4 shadow-sm', bgColor)}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className={cn('mt-1 text-3xl font-bold', textColor)}>{value}</p>
          {sub && <p className="mt-1 text-xs text-muted-foreground">{sub}</p>}
        </div>
        <div className={cn('rounded-full p-3', textColor)}>{icon}</div>
      </div>
    </div>
  )
}

interface HeartbeatBadgeProps {
  running: boolean
  lastRunAt: string | null
  nextRunAt: string | null
}

function HeartbeatBadge({ running, lastRunAt, nextRunAt }: HeartbeatBadgeProps) {
  return (
    <div className={cn(
      'rounded-lg border-2 p-4 shadow-sm',
      running ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
    )}>
      <div className="flex items-center gap-3">
        {running ? (
          <CheckCircle2 className="h-8 w-8 text-green-600" />
        ) : (
          <XCircle className="h-8 w-8 text-red-600" />
        )}
        <div>
          <p className={cn('font-bold text-lg', running ? 'text-green-700' : 'text-red-700')}>
            Heartbeat {running ? '稼働中' : '停止中'}
          </p>
          <p className="text-xs text-muted-foreground">
            前回: {formatDateTime(lastRunAt)} / 次回: {formatDateTime(nextRunAt)}
          </p>
        </div>
      </div>
    </div>
  )
}

// ─── メインページ ───

type PeriodDays = 7 | 30

export function MonitoringPage() {
  const [periodDays, setPeriodDays] = useState<PeriodDays>(7)

  const {
    data: metricsData,
    isLoading: metricsLoading,
    error: metricsError,
    refetch: refetchMetrics,
  } = useMonitoringMetrics(periodDays)

  const {
    data: healthData,
    isLoading: healthLoading,
    error: healthError,
    refetch: refetchHealth,
  } = useMonitoringHealth()

  const summary = metricsData?.summary
  const health = healthData

  // ─── ステータス判定 ───

  const acceptanceStatus = useMemo((): 'good' | 'warning' | 'danger' => {
    if (!summary) return 'warning'
    if (summary.proposalAcceptanceRate >= 0.6) return 'good'
    if (summary.proposalAcceptanceRate >= 0.4) return 'warning'
    return 'danger'
  }, [summary])

  const errorStatus = useMemo((): 'good' | 'warning' | 'danger' => {
    if (!summary) return 'warning'
    if (summary.errorRate <= 0.02) return 'good'
    if (summary.errorRate <= 0.05) return 'warning'
    return 'danger'
  }, [summary])

  const overallStatus = health?.status ?? 'degraded'

  // ─── エラー別棒グラフデータ ───

  const errorBarData = useMemo(() => {
    if (!summary?.errorsByComponent) return []
    return Object.entries(summary.errorsByComponent)
      .map(([component, count]) => ({ component, count: count ?? 0 }))
      .filter((d) => d.count > 0)
      .sort((a, b) => b.count - a.count)
  }, [summary])

  const isLoading = metricsLoading || healthLoading
  const hasError = metricsError || healthError

  const handleRefresh = () => {
    void refetchMetrics()
    void refetchHealth()
  }

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">モニタリング</h1>
          <p className="text-muted-foreground">Heartbeat 稼働状況と計測メトリクス</p>
        </div>
        <div className="flex items-center gap-3">
          {/* 期間切り替え */}
          <div className="flex rounded-lg border overflow-hidden">
            {([7, 30] as PeriodDays[]).map((d) => (
              <button
                key={d}
                onClick={() => setPeriodDays(d)}
                className={cn(
                  'px-4 py-2 text-sm font-medium transition-colors',
                  periodDays === d
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background text-muted-foreground hover:bg-muted'
                )}
              >
                {d === 7 ? '7日' : '30日'}
              </button>
            ))}
          </div>

          <button
            onClick={handleRefresh}
            disabled={isLoading}
            className="flex items-center gap-2 rounded-lg border px-4 py-2 text-sm hover:bg-muted disabled:opacity-50"
          >
            <RefreshCw className={cn('h-4 w-4', isLoading && 'animate-spin')} />
            更新
          </button>
        </div>
      </div>

      {/* エラー表示 */}
      {hasError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 flex items-center gap-2 text-red-700">
          <AlertCircle className="h-5 w-5" />
          <span className="text-sm">データの取得に失敗しました。</span>
        </div>
      )}

      {/* Heartbeat ステータス */}
      <section className="space-y-3">
        <h2 className="text-lg font-semibold">稼働ステータス</h2>
        {healthLoading ? (
          <div className="h-24 rounded-lg border bg-card animate-pulse" />
        ) : health ? (
          <HeartbeatBadge
            running={health.heartbeat.running}
            lastRunAt={health.heartbeat.lastRunAt}
            nextRunAt={health.heartbeat.nextRunAt}
          />
        ) : null}

        {health && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">総合ステータス:</span>
            <span className={cn(
              'rounded-full px-3 py-1 text-xs font-medium',
              overallStatus === 'healthy' && 'bg-green-100 text-green-700',
              overallStatus === 'degraded' && 'bg-yellow-100 text-yellow-700',
              overallStatus === 'down' && 'bg-red-100 text-red-700',
            )}>
              {overallStatus === 'healthy' ? '正常' : overallStatus === 'degraded' ? '低下' : '停止'}
            </span>
          </div>
        )}
      </section>

      {/* メトリクス カード */}
      <section className="space-y-3">
        <h2 className="text-lg font-semibold">計測メトリクス（過去{periodDays}日）</h2>
        {metricsLoading ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-28 rounded-lg border bg-card animate-pulse" />
            ))}
          </div>
        ) : summary ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <MetricCard
              title="提案採択率"
              value={formatPercentage(summary.proposalAcceptanceRate)}
              sub={`承認 ${summary.proposalApprovedCount} / 却下 ${summary.proposalRejectedCount}`}
              status={acceptanceStatus}
              icon={<TrendingUp className="h-6 w-6" />}
            />
            <MetricCard
              title="サマリー品質"
              value={formatPercentage(summary.avgSummaryQualityScore)}
              sub={`Cognify活用率 ${formatPercentage(summary.cognifyDataUsageRate)}`}
              status={summary.avgSummaryQualityScore >= 0.7 ? 'good' : 'warning'}
              icon={<Activity className="h-6 w-6" />}
            />
            <MetricCard
              title="対話解決率"
              value={formatPercentage(summary.dialogueResolutionRate)}
              sub={`平均ターン数 ${summary.avgDialogueTurns.toFixed(1)}`}
              status={summary.dialogueResolutionRate >= 0.7 ? 'good' : 'warning'}
              icon={<CheckCircle2 className="h-6 w-6" />}
            />
            <MetricCard
              title="エラー率"
              value={formatPercentage(summary.errorRate)}
              sub="エラー率 2% 以下が目標"
              status={errorStatus}
              icon={<AlertCircle className="h-6 w-6" />}
            />
          </div>
        ) : null}
      </section>

      {/* コンポーネント別エラー棒グラフ */}
      {summary && errorBarData.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-lg font-semibold">コンポーネント別エラー数</h2>
          <div className="rounded-lg border bg-card p-4 shadow-sm">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={errorBarData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="component" tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="count" name="エラー数" fill="#ef4444" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>
      )}

      {/* Cognify 抽出統計 */}
      {summary && (
        <section className="space-y-3">
          <h2 className="text-lg font-semibold">Cognify 抽出統計</h2>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-lg border bg-card p-4 shadow-sm">
              <p className="text-sm text-muted-foreground">平均エンティティ抽出数</p>
              <p className="mt-2 text-3xl font-bold">{summary.avgEntityCount.toFixed(1)}</p>
              <p className="text-xs text-muted-foreground">件 / テキスト</p>
            </div>
            <div className="rounded-lg border bg-card p-4 shadow-sm">
              <p className="text-sm text-muted-foreground">平均抽出処理時間</p>
              <p className="mt-2 text-3xl font-bold">{summary.avgExtractionDurationMs.toFixed(0)}</p>
              <p className="text-xs text-muted-foreground">ms / テキスト</p>
            </div>
          </div>
        </section>
      )}
    </div>
  )
}
